/**
 * Exclusions of the ad campaign in the wizard: `audienceTargeting.exclusions`, `{ existing_leads, purchasers }`.
 * They apply to acquisition audiences only, each for its own objective, and are off unless chosen.
 */

export const EXCLUSION_STAGE = 'acquisition'

export const EXCLUSION_OPTIONS = {
    existing_leads: {
        objective: 'leads',
        label: 'Exclude people who already submitted a lead',
        description:
            "People who submitted one of this licence's Instant Forms in the last 90 days will not see the acquisition ads, so the budget goes to new people. Retargeting stages are not affected.",
    },
    purchasers: {
        objective: 'sales',
        label: 'Exclude people who already purchased',
        description:
            'People with a purchase recorded by your pixel in the last 180 days will not see the acquisition ads. Retargeting and retention stages are not affected.',
    },
}

export const EXCLUSION_CREATION_NOTE =
    'Meta builds this audience when the campaign is published and fills it over time; a new audience is used while Meta is still populating it. If Meta reports it as not usable (for example too small or flagged), the campaign is published without it and you will see a warning.'

export const EXCLUSION_STATUS_TEXT = {
    ready: 'Ready',
    populating: 'Ready, Meta is still populating it',
    not_created: 'Will be created when the campaign is published',
    not_available: 'Not available',
    too_small: 'Too small to use yet',
    flagged: 'Flagged by Meta, it will not be used',
    unusable: 'Not usable on Meta',
    being_prepared: 'Being prepared',
    unknown: 'Status could not be checked',
}

/** Exclusion kinds offered for an objective. */
export const exclusionKindsForObjective = (objective) =>
    Object.keys(EXCLUSION_OPTIONS).filter(
        (kind) => EXCLUSION_OPTIONS[kind].objective === String(objective || '').toLowerCase(),
    )

/** The stored choice, reduced to kinds set to true. */
export const getExclusionChoice = (audienceTargetingData) => {
    const value = audienceTargetingData?.exclusions
    if (!value || typeof value !== 'object') return {}
    return Object.keys(EXCLUSION_OPTIONS).reduce((choice, kind) => {
        if (value[kind] === true) choice[kind] = true
        return choice
    }, {})
}

/** Exclusions to submit with an audience, or undefined to send none. */
export const exclusionsForSubmission = (audienceTargetingData, stageId, objective) => {
    if (stageId !== EXCLUSION_STAGE) return undefined
    const choice = getExclusionChoice(audienceTargetingData)
    const kinds = exclusionKindsForObjective(objective).filter((kind) => choice[kind])
    if (kinds.length === 0) return undefined
    return kinds.reduce((result, kind) => ({ ...result, [kind]: true }), {})
}

/** One line about an exclusion audience for the wizard, from the server's read-only status. */
export const exclusionStatusLine = (source) => {
    if (!source) return null
    const status = EXCLUSION_STATUS_TEXT[source.status] || EXCLUSION_STATUS_TEXT.unknown
    const size =
        typeof source.approximate_count_lower === 'number'
            ? ` · about ${source.approximate_count_lower.toLocaleString('en-US')}+ people`
            : ''
    const reason = source.message ? ` (${source.message})` : ''
    return `${status}${size}${reason}`
}
